import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { 
  Plus, 
  Trash2, 
  GripVertical, 
  ChevronDown, 
  ChevronRight,
  Save,
  Menu as MenuIcon,
  ExternalLink,
  Link as LinkIcon,
  FileText,
  MapPin,
  Edit
} from 'lucide-react';

const AdminMenus = () => {
  const { admin } = useAuth();
  const [mainMenu, setMainMenu] = useState({ id: 'main', items: [] });
  const [footerMenu, setFooterMenu] = useState({ id: 'footer', items: [] });
  const [pages, setPages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('main');
  
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [parentId, setParentId] = useState(null);
  const [newItem, setNewItem] = useState({
    label: '',
    link_type: 'url',
    link_value: '',
    open_in_new_tab: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [menusRes, pagesRes, locationsRes] = await Promise.all([
        axios.get(`${API}/cms/menus`),
        axios.get(`${API}/cms/pages?published_only=false`),
        axios.get(`${API}/locations`)
      ]);
      
      const menus = menusRes.data;
      const main = menus.find(m => m.id === 'main') || { id: 'main', items: [] };
      const footer = menus.find(m => m.id === 'footer') || { id: 'footer', items: [] };
      
      setMainMenu(main);
      setFooterMenu(footer);
      setPages(pagesRes.data);
      setLocations(locationsRes.data);
    } catch (err) {
      toast.error('Fehler beim Laden der Menüs');
    } finally {
      setLoading(false);
    }
  };

  const saveMenu = async (menuId) => {
    setSaving(true);
    const menu = menuId === 'main' ? mainMenu : footerMenu;
    
    try {
      await axios.put(`${API}/cms/menus/${menuId}`, { items: menu.items }, {
        headers: { Authorization: `Bearer ${admin.token}` }
      });
      toast.success('Menü gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const openAddDialog = (parentItemId = null) => {
    setParentId(parentItemId);
    setNewItem({ label: '', link_type: 'url', link_value: '', open_in_new_tab: false });
    setEditingItem(null);
    setShowAddItem(true);
  };

  const openEditDialog = (item, parentItemId = null) => {
    setParentId(parentItemId);
    setNewItem({
      label: item.label,
      link_type: item.link_type,
      link_value: item.link_value,
      open_in_new_tab: item.open_in_new_tab || false
    });
    setEditingItem(item);
    setShowAddItem(true);
  };

  const handleAddOrUpdateItem = () => {
    if (!newItem.label) {
      toast.error('Bitte Label angeben');
      return;
    }

    const menuSetter = activeTab === 'main' ? setMainMenu : setFooterMenu;
    const menu = activeTab === 'main' ? mainMenu : footerMenu;

    const itemData = {
      id: editingItem?.id || Date.now().toString(),
      label: newItem.label,
      link_type: newItem.link_type,
      link_value: newItem.link_value,
      children: editingItem?.children || [],
      order: editingItem?.order || 0,
      is_active: true,
      open_in_new_tab: newItem.open_in_new_tab
    };

    if (editingItem) {
      menuSetter({
        ...menu,
        items: updateItemInTree(menu.items, editingItem.id, itemData)
      });
    } else {
      if (parentId) {
        menuSetter({
          ...menu,
          items: addChildToItem(menu.items, parentId, itemData)
        });
      } else {
        menuSetter({
          ...menu,
          items: [...menu.items, { ...itemData, order: menu.items.length }]
        });
      }
    }

    setShowAddItem(false);
    setEditingItem(null);
    setParentId(null);
  };

  const updateItemInTree = (items, itemId, newData) => {
    return items.map(item => {
      if (item.id === itemId) {
        return { ...item, ...newData };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: updateItemInTree(item.children, itemId, newData) };
      }
      return item;
    });
  };

  const addChildToItem = (items, pId, child) => {
    return items.map(item => {
      if (item.id === pId) {
        return { ...item, children: [...(item.children || []), child] };
      }
      if (item.children && item.children.length > 0) {
        return { ...item, children: addChildToItem(item.children, pId, child) };
      }
      return item;
    });
  };

  const deleteItem = (itemId) => {
    if (!window.confirm('Menüpunkt wirklich löschen?')) return;

    const menuSetter = activeTab === 'main' ? setMainMenu : setFooterMenu;
    const menu = activeTab === 'main' ? mainMenu : footerMenu;

    const deleteFromTree = (items) => {
      return items.filter(item => item.id !== itemId).map(item => ({
        ...item,
        children: item.children ? deleteFromTree(item.children) : []
      }));
    };

    menuSetter({ ...menu, items: deleteFromTree(menu.items) });
  };

  const moveItem = (itemId, direction) => {
    const menuSetter = activeTab === 'main' ? setMainMenu : setFooterMenu;
    const menu = activeTab === 'main' ? mainMenu : footerMenu;

    const moveInArray = (items) => {
      const index = items.findIndex(i => i.id === itemId);
      if (index === -1) {
        return items.map(item => ({
          ...item,
          children: item.children ? moveInArray(item.children) : []
        }));
      }

      const newItems = [...items];
      if (direction === 'up' && index > 0) {
        [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
      } else if (direction === 'down' && index < items.length - 1) {
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      }
      return newItems;
    };

    menuSetter({ ...menu, items: moveInArray(menu.items) });
  };

  const getLinkTypeIcon = (type) => {
    switch (type) {
      case 'page': return FileText;
      case 'location': return MapPin;
      case 'dropdown': return ChevronDown;
      default: return LinkIcon;
    }
  };

  const getLinkDisplay = (item) => {
    switch (item.link_type) {
      case 'page':
        const page = pages.find(p => p.slug === item.link_value);
        return page ? `/seite/${page.slug}` : item.link_value;
      case 'location':
        return `/standorte/${item.link_value}`;
      case 'dropdown':
        return `${item.children?.length || 0} Untermenüs`;
      default:
        return item.link_value;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const currentMenu = activeTab === 'main' ? mainMenu : footerMenu;

  return (
    <div className="space-y-6" data-testid="admin-menus">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Menüs verwalten</h1>
          <p className="text-slate-500">Verwalten Sie Haupt- und Footer-Menü</p>
        </div>
        <Button 
          onClick={() => saveMenu(activeTab)} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-menu-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Speichern...' : 'Menü speichern'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="main" data-testid="tab-main-menu">
            <MenuIcon className="w-4 h-4 mr-2" />
            Hauptmenü
          </TabsTrigger>
          <TabsTrigger value="footer" data-testid="tab-footer-menu">
            <MenuIcon className="w-4 h-4 mr-2" />
            Footer Menü
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{activeTab === 'main' ? 'Hauptmenü' : 'Footer Menü'}</CardTitle>
                <CardDescription>
                  {activeTab === 'main' 
                    ? 'Navigation oben auf der Website'
                    : 'Links im Footer-Bereich'
                  }
                </CardDescription>
              </div>
              <Button onClick={() => openAddDialog()} variant="outline" data-testid="add-menu-item">
                <Plus className="w-4 h-4 mr-2" />
                Menüpunkt hinzufügen
              </Button>
            </CardHeader>
            <CardContent>
              {currentMenu.items.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <MenuIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Noch keine Menüpunkte vorhanden</p>
                  <Button onClick={() => openAddDialog()} variant="link">
                    Ersten Menüpunkt hinzufügen
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {currentMenu.items.map((item, index) => (
                    <div key={item.id}>
                      <MenuItem
                        item={item}
                        index={index}
                        total={currentMenu.items.length}
                        onEdit={openEditDialog}
                        onDelete={deleteItem}
                        onMoveUp={() => moveItem(item.id, 'up')}
                        onMoveDown={() => moveItem(item.id, 'down')}
                        onAddChild={openAddDialog}
                        getLinkTypeIcon={getLinkTypeIcon}
                        getLinkDisplay={getLinkDisplay}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Menüpunkt bearbeiten' : (parentId ? 'Untermenü hinzufügen' : 'Menüpunkt hinzufügen')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={newItem.label}
                onChange={(e) => setNewItem({...newItem, label: e.target.value})}
                placeholder="z.B. Über uns"
                data-testid="menu-item-label"
              />
            </div>
            <div className="space-y-2">
              <Label>Link-Typ</Label>
              <Select 
                value={newItem.link_type} 
                onValueChange={(v) => setNewItem({...newItem, link_type: v, link_value: ''})}
              >
                <SelectTrigger data-testid="menu-item-link-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="url">URL / Pfad</SelectItem>
                  <SelectItem value="page">CMS Seite</SelectItem>
                  <SelectItem value="location">Standort</SelectItem>
                  {activeTab === 'main' && !parentId && (
                    <SelectItem value="dropdown">Dropdown (mit Untermenüs)</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {newItem.link_type === 'url' && (
              <div className="space-y-2">
                <Label>URL / Pfad</Label>
                <Input
                  value={newItem.link_value}
                  onChange={(e) => setNewItem({...newItem, link_value: e.target.value})}
                  placeholder="z.B. /kontakt oder https://..."
                  data-testid="menu-item-url"
                />
              </div>
            )}

            {newItem.link_type === 'page' && (
              <div className="space-y-2">
                <Label>CMS Seite auswählen</Label>
                <Select 
                  value={newItem.link_value} 
                  onValueChange={(v) => setNewItem({...newItem, link_value: v})}
                >
                  <SelectTrigger data-testid="menu-item-page">
                    <SelectValue placeholder="Seite auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {pages.map(page => (
                      <SelectItem key={page.slug} value={page.slug}>
                        {page.title} {!page.published && '(Entwurf)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {newItem.link_type === 'location' && (
              <div className="space-y-2">
                <Label>Standort auswählen</Label>
                <Select 
                  value={newItem.link_value} 
                  onValueChange={(v) => setNewItem({...newItem, link_value: v})}
                >
                  <SelectTrigger data-testid="menu-item-location">
                    <SelectValue placeholder="Standort auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map(loc => (
                      <SelectItem key={loc.slug} value={loc.slug}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {newItem.link_type === 'dropdown' && (
              <p className="text-sm text-slate-500">
                Bei einem Dropdown-Menü können Sie nach dem Erstellen Untermenüpunkte hinzufügen.
              </p>
            )}

            {newItem.link_type !== 'dropdown' && (
              <div className="flex items-center justify-between">
                <div>
                  <Label>In neuem Tab öffnen</Label>
                  <p className="text-sm text-slate-500">Link öffnet ein neues Browserfenster</p>
                </div>
                <Switch
                  checked={newItem.open_in_new_tab}
                  onCheckedChange={(v) => setNewItem({...newItem, open_in_new_tab: v})}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Abbrechen</Button>
            </DialogClose>
            <Button onClick={handleAddOrUpdateItem} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-menu-item">
              {editingItem ? 'Speichern' : 'Hinzufügen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Separate MenuItem component without recursion issue
const MenuItem = ({ 
  item, 
  index, 
  total, 
  onEdit, 
  onDelete, 
  onMoveUp, 
  onMoveDown, 
  onAddChild,
  getLinkTypeIcon,
  getLinkDisplay,
  isChild = false
}) => {
  const [expanded, setExpanded] = useState(true);
  const Icon = getLinkTypeIcon(item.link_type);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div className={isChild ? 'ml-8' : ''}>
      <div 
        className={`flex items-center gap-3 p-3 rounded-lg border ${
          isChild ? 'bg-slate-50' : 'bg-white'
        } hover:shadow-sm transition-shadow`}
        data-testid={`menu-item-${item.id}`}
      >
        <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
        
        {item.link_type === 'dropdown' && (
          <button onClick={() => setExpanded(!expanded)} className="p-1">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        )}
        
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            <span className="font-medium text-slate-900 block truncate">{item.label}</span>
            <span className="text-xs text-slate-500 truncate block">{getLinkDisplay(item)}</span>
          </div>
        </div>

        {item.open_in_new_tab && (
          <ExternalLink className="w-4 h-4 text-slate-400" />
        )}

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onMoveUp} disabled={index === 0}>
            <ChevronDown className="w-4 h-4 rotate-180" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onMoveDown} disabled={index === total - 1}>
            <ChevronDown className="w-4 h-4" />
          </Button>
          {item.link_type === 'dropdown' && (
            <Button variant="ghost" size="sm" onClick={() => onAddChild(item.id)} title="Untermenü hinzufügen">
              <Plus className="w-4 h-4" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onEdit(item)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDelete(item.id)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {hasChildren && expanded && (
        <div className="mt-2 space-y-2">
          {item.children.map((child, childIndex) => (
            <ChildMenuItem
              key={child.id}
              item={child}
              index={childIndex}
              total={item.children.length}
              parentId={item.id}
              onEdit={onEdit}
              onDelete={onDelete}
              getLinkTypeIcon={getLinkTypeIcon}
              getLinkDisplay={getLinkDisplay}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Simple child menu item (no further recursion)
const ChildMenuItem = ({ 
  item, 
  index, 
  total, 
  parentId,
  onEdit, 
  onDelete, 
  getLinkTypeIcon,
  getLinkDisplay
}) => {
  const Icon = getLinkTypeIcon(item.link_type);

  return (
    <div className="ml-8">
      <div 
        className="flex items-center gap-3 p-3 rounded-lg border bg-slate-50 hover:shadow-sm transition-shadow"
        data-testid={`menu-item-${item.id}`}
      >
        <GripVertical className="w-4 h-4 text-slate-400 cursor-move" />
        
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4 text-slate-500" />
          </div>
          <div className="min-w-0">
            <span className="font-medium text-slate-900 block truncate">{item.label}</span>
            <span className="text-xs text-slate-500 truncate block">{getLinkDisplay(item)}</span>
          </div>
        </div>

        {item.open_in_new_tab && (
          <ExternalLink className="w-4 h-4 text-slate-400" />
        )}

        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(item, parentId)}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDelete(item.id)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminMenus;
